import { useEffect, useState } from 'react';
import { IconBell, IconChecklist, IconChecks, IconClock, IconClockHour4, IconLayoutDashboard, IconTool, IconFilter, IconMaximize, IconPlus, IconSearch, IconLock, IconEye, IconFileDollar, IconBell as IconBellAct, IconSend, IconExternalLink, IconCalendar, IconMoon } from '@tabler/icons-react';
import { IconSparkles } from '@tabler/icons-react';
import { useSearch } from '@tanstack/react-router';
import { useT } from '@hana/i18n';
import { HeaderTool, Page, PageHeader } from '@hana/shell';
import { Button, Segmented, Skeleton, StateView, useTheme, useToast, type ScreenState } from '@hana/ui';
import { GRP_LABEL, MY_DONE, MY_TASKS, BRIEF_CHIPS, BRIEF_SALE, KPI_SALE, kpiHist, spark, type Grp, type Kpi, type MyTask } from './data';
import s from './home.module.css';
import './i18n';
import { MyPhone } from './MyPhone';
import { Approvals } from './Approvals';
import { News } from './News';
import { Team } from './Team';
import { Given } from './Given';
import { Deleg } from './Deleg';
import { Side } from './Side';
import { HomeDock, useHomeDock } from './Dock';

// Mint-Home (1536) · N-04a: Tóm tắt đầu ngày + 4 chỉ số (vai sale). Các khối khác: N-04b…e. Không hiện tiền.
const STATES: readonly ScreenState[] = ['ready', 'loading', 'empty', 'error', 'noperm'];
/** Cổng chặn việc (board: gate 2 = Chặn, 1 = Cảnh báo) — id việc → mức. */
const GATE: Record<number, 1 | 2> = { 1: 2, 2: 1 };
const CHIP = { n: s.cn, err: s.cerr, pri: s.cpri, ok: s.cn, warn: s.cn } as const;
const TONE = { ok: s.kok, warn: s.kwarn, err: s.kerr, n: s.kn } as const;

function Tile({ k, empty }: { k: Kpi; empty: boolean }) {
  const t = useT();
  const num = parseFloat(k.v.replace(',', '.')) || 0;
  const pct = k.u === '%';
  const h = kpiHist(num);
  const d = spark(h, 120, 28);
  const aria = empty ? '' : pct ? t('home.kpi.target', { k: k.k, v: k.v, u: k.u }) : t('home.kpi.hist', { k: k.k, v: k.v, u: k.u, h: h.join(', ') });
  return (
    <section className={s.kpi} aria-label={k.k}>
      <div className={s.kLb}>{k.k}</div>
      <div className={s.kV}><b>{empty ? '0' : k.v}</b> <span>{k.u}</span></div>
      <span className={`${s.kD} ${TONE[empty ? 'n' : k.tone]}`}>{empty ? t('home.kpi.noData') : k.d}</span>
      {!empty && (pct ? (
        <div className={s.ticks} role="img" aria-label={aria}>{Array.from({ length: 24 }, (_, i) => <i key={i} className={i < Math.round((num / 100) * 24) ? s.on : undefined} />)}</div>
      ) : (
        <svg className={s.sp} role="img" aria-label={aria} viewBox="0 0 120 28" preserveAspectRatio="none"><path className={s.area} d={`${d} L118 28 L2 28 Z`} /><path className={s.line} d={d} /></svg>
      ))}
    </section>
  );
}


const CODE_CLS = (c: string) => (c === 'GPW' || c === 'DUN' ? s.e : c === 'MEMO' || c === '@' ? s.m : ['PRC', 'SLD', 'OPF', 'DCF', 'QAP'].includes(c) ? s.p : '');
const ACT_IC: Record<string, typeof IconLock> = { 'Khoá cước': IconLock, 'Xem duyệt giá': IconEye, 'Báo giá': IconFileDollar, 'Nhắc nợ': IconBellAct, 'Nhắc khách': IconBellAct, 'Gửi báo khách': IconSend };
// Rút gọn như board (CONCISE AUG): "Quá 3h" → "−3h", "Còn 2h 10′" → "2h 10′"; ngày bỏ năm.
const dueS = (k: string) => k.replace(/^(?:Quá hạn|Quá|Trễ)\s+(.+)$/i, '−$1').replace(/^Còn\s+(?=[\d−-])/i, '');
const dueD = (d: string) => d.replace(/^(\d{1,2}\/[A-Za-z]{3})\/\d{4}/, '$1');
const LINK: Record<string, string> = { FRT: '/ezy/jobs', VCN: '/ezy/jobs', BKO: '/ezy/jobs', DUN: '/ezy/ar', DCF: '/ezy/ar', PRC: '/ezy/quotes', GPW: '/ezy/quotes' };

function MyRow({ x, pick, onPick, tip }: { x: MyTask; pick: boolean; onPick: () => void; tip: (m: string) => void }) {
  const t = useT();
  const over = x.grp === 'over';
  return (
    <div className={s.row}>
      <input type="checkbox" aria-label={t('home.pick', { t: x.title })} checked={pick} onChange={onPick} />
      <span className={`${s.code} ${CODE_CLS(x.code)}`} title={x.code}>{x.code}</span>
      <div className={s.rb}>
        <div className={s.rt}>{x.title}{GATE[x.id] && <span className={`${s.gate} ${GATE[x.id] === 2 ? s.cerr : s.kwarn}`}>{GATE[x.id] === 2 ? t('home.gate.block') : t('home.gate.warn')}</span>}</div>
        <div className={s.rs}>
          {x.obj && <a href={LINK[x.code] ?? '/ezy/jobs'}>{x.obj}</a>}
          <span className={`${s.due} ${over ? s.over : ''}`} title={x.dueK} aria-label={x.dueK}><IconClockHour4 size={13} stroke={1.8} aria-hidden="true" />{dueS(x.dueK)}</span>
          <span className={s.dd} title={t('home.due', { d: x.due })}><IconCalendar size={13} stroke={1.8} aria-hidden="true" />{dueD(x.due)}</span>
        </div>
      </div>
      <div className={s.ra}>
        {x.act && (() => { const Ic = ACT_IC[x.act] ?? IconExternalLink; return <a href={LINK[x.code] ?? '/ezy/jobs'}><Ic size={16} stroke={1.8} aria-hidden="true" />{x.act}</a>; })()}
        <button type="button" className={s.bt} aria-label={t('home.ext', { x: x.ext })} onClick={() => tip(t('home.extDone'))}><IconPlus size={16} stroke={1.8} aria-hidden="true" />4h</button>
        <button type="button" className={s.ib} aria-label={t('home.more', { t: x.title })} aria-haspopup="menu" onClick={() => tip(t('home.later'))}>⋯</button>
      </div>
    </div>
  );
}

function MyTasks({ empty }: { empty: boolean }) {
  const t = useT();
  const toast = useToast();
  const [view, setView] = useState<'open' | 'done'>('open');
  const [q, setQ] = useState('');
  const [all, setAll] = useState(false);
  const [picked, setPicked] = useState<readonly number[]>([]);
  const tip = (m: string) => toast({ msg: m });
  const norm = (v: string) => v.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
  const open = empty ? [] : MY_TASKS.filter((x) => !q || norm(`${x.title} ${x.obj} ${x.sub}`).includes(norm(q)));
  const shown = all ? open : open.slice(0, 8);
  const groups = (['over', 'today', 'next'] as Grp[]).map((g) => ({ g, items: shown.filter((x) => x.grp === g), n: open.filter((x) => x.grp === g).length })).filter((g) => g.items.length);
  const total = empty ? 0 : MY_TASKS.length;
  return (
    <section className={s.my} aria-label={t('home.my')}>
      <div className={s.myH}>
        <h2>{t('home.my')}</h2><span className={s.sum}>{t('home.myCount', { n: total, o: empty ? 0 : MY_TASKS.filter((x) => x.grp === 'over').length })}</span>
        <span className={s.grow} />
        <button type="button" className={s.ib} aria-label={t('home.multi')} title={t('home.multi')} onClick={() => setPicked(picked.length ? [] : shown.map((x) => x.id))}><IconChecks size={16} stroke={1.8} aria-hidden="true" /></button>
        <button type="button" className={`${s.bt} ${s.pri}`} aria-haspopup="dialog" onClick={() => tip(t('home.later'))}><IconPlus size={16} stroke={1.8} aria-hidden="true" />{t('home.note')}</button>
        <button type="button" className={s.ib} aria-label={t('home.expandLabel')} title={t('home.expand')} onClick={() => tip(t('home.later'))}><IconMaximize size={15} stroke={2} aria-hidden="true" /></button>
      </div>
      <div className={s.tools}>
        <div className={s.seg} role="group" aria-label={t('home.status')}>
          <button type="button" aria-pressed={view === 'open'} onClick={() => setView('open')}>{t('home.open', { n: total })}</button>
          <button type="button" aria-pressed={view === 'done'} onClick={() => setView('done')}>{t('home.done', { n: empty ? 0 : MY_DONE })}</button>
        </div>
        <label className={s.find}><IconSearch size={14} stroke={2.2} aria-hidden="true" /><input aria-label={t('home.findLabel')} placeholder={t('home.findPh')} value={q} onChange={(e) => setQ(e.target.value)} /></label>
        <button type="button" className={s.bt} aria-haspopup="dialog" onClick={() => tip(t('home.later'))}><IconFilter size={16} stroke={1.8} aria-hidden="true" />{t('home.filter')}</button>
      </div>
      {view === 'done' ? <div className={s.none}>{empty ? t('home.noneDone') : t('home.doneN', { n: MY_DONE })}</div> : !groups.length ? <div className={s.none}>{q ? t('home.noMatch') : t('home.noToday')}</div> : groups.map(({ g, items, n }) => (
        <div key={g}>
          <div className={`${s.gh} ${g === 'over' ? s.over : ''}`}>{GRP_LABEL[g]} · {n}</div>
          {items.map((x) => <MyRow key={x.id} x={x} tip={tip} pick={picked.includes(x.id)} onPick={() => setPicked((p) => (p.includes(x.id) ? p.filter((i) => i !== x.id) : [...p, x.id]))} />)}
        </div>
      ))}
      {view === 'open' && !all && open.length > shown.length && <button type="button" className={s.more} onClick={() => setAll(true)}>{t('home.seeMore', { n: open.length - shown.length })}</button>}
    </section>
  );
}

// Khổ iPad (Mint-Home-iPad @1180): đầu trang chỉ Tìm · Thông báo · Sửa bố cục · Giao diện tối · Báo giá; banner tin thu gọn một dòng.
function useMedia(q: string) {
  const [on, setOn] = useState(() => typeof window !== 'undefined' && window.matchMedia(q).matches);
  useEffect(() => {
    const m = window.matchMedia(q);
    const f = () => setOn(m.matches);
    f();
    m.addEventListener('change', f);
    return () => m.removeEventListener('change', f);
  }, [q]);
  return on;
}
const useTablet = () => useMedia('(max-width: 1279px)');
const usePhone = () => useMedia('(max-width: 719px)');

export function Home() {
  const t = useT();
  const toast = useToast();
  const q = (useSearch({ strict: false }) as { state?: string }).state;
  const state: ScreenState = STATES.find((x) => x === q) ?? 'ready';
  const empty = state === 'empty';
  const dock = useHomeDock();
  const tablet = useTablet();
  const phone = usePhone();
  const { toggle: toggleTheme } = useTheme();
  const [dens, setDens] = useState<'std' | 'tight'>('std');
  const soon = (m: string) => () => toast({ msg: t('home.soon', { m }) });
  return (
    <Page kind="table" dock={<HomeDock dock={dock} empty={empty} />}>
      <PageHeader findPad
        minH={tablet || phone ? undefined : 61}
        title={t('home.title')}
        sub={phone ? undefined : t('home.sub', { d: '22/Oct/2026', n: 'DAVID', r: 'Trưởng phòng Kinh doanh' })}
        actions={
          <>
            {tablet ? (
              <>
                <HeaderTool label={t('home.tool.notiN', { n: 2 })} icon={IconBell} n={2} onClick={() => dock.toggle('noti')} />
                <HeaderTool label={t('home.tool.layout')} icon={IconLayoutDashboard} onClick={soon(t('home.tool.layoutShort'))} />
                <HeaderTool label={t('home.tool.dark')} icon={IconMoon} onClick={toggleTheme} />
              </>
            ) : (
              <>
            <Segmented kind="toggle" label={t('home.density')} value={dens} onChange={setDens} items={[{ key: 'std', label: t('home.dense.std') }, { key: 'tight', label: t('home.dense.tight') }]} />
            <HeaderTool label={t('home.tool.clock')} icon={IconClock} onClick={soon(t('home.tool.clock'))} />
            <HeaderTool label={t('home.tool.util')} icon={IconTool} onClick={soon(t('home.tool.util'))} />
            <HeaderTool label={t('home.tool.layout')} icon={IconLayoutDashboard} onClick={soon(t('home.tool.layoutShort'))} />
            <HeaderTool label={t('home.tool.noti')} icon={IconBell} n={2} onClick={() => dock.toggle('noti')} />
            <HeaderTool label={t('home.tool.task')} icon={IconChecklist} n={15} onClick={() => dock.toggle('task')} />
              </>
            )}
            <Button variant="primary" icon={<IconPlus size={16} aria-hidden="true" />} onClick={soon(t('home.quote'))}>{t('home.quote')}</Button>
          </>
        }
      />
      {(state === 'ready' || empty) && <News empty={empty} compact={tablet} />}
      <div className={`${s.page} ${s.desk}`}>
        <div className={s.main}><StateView state={state} skeleton={<Skeleton rows={6} />}>{phone ? <MyPhone empty={empty} /> : <MyTasks empty={empty} />}<Approvals empty={empty} /><Team empty={empty} /><Given empty={empty} /><Deleg empty={empty} /></StateView></div>
        <div className={s.side}>
          <StateView state={state} skeleton={<Skeleton rows={6} />}>
            {!dock.open && <section className={s.brief} aria-label={t('home.brief')}>
              <span className={s.bIco} aria-hidden="true"><IconSparkles size={20} stroke={1.8} /></span>
              <div className={s.bTx}><h2>{t('home.brief')}</h2><span>{t('home.briefSrc')}</span></div>
              <button type="button" className={s.ask} onClick={() => toast({ msg: t('home.later') })}>{t('home.askHana')}</button>
              <div className={s.bBody}>
                <p>{empty ? t('home.briefNone') : BRIEF_SALE}</p>
                <div className={s.chips}>
                  {empty ? <span className={`${s.chip} ${s.cn}`}>{t('home.noTaskToday')}</span> : BRIEF_CHIPS.map((c) => <span key={c.k} className={`${s.chip} ${CHIP[c.tone]}`} title={c.k}>{c.k}</span>)}
                </div>
              </div>
            </section>}
            <div className={s.kpis}>{KPI_SALE.map((k) => <Tile key={k.k} k={k} empty={empty} />)}</div>
            <Side empty={empty} />
          </StateView>
        </div>
      </div>
    </Page>
  );
}
